import { Label } from '@/components/ui-kit/label';
import { resolveTemplateVariables } from '../utils';
import { Input } from '@/components/ui-kit/input';
import { cn } from '@/lib/utils';
import AISuggestionIndicator from './AISuggestionIndicator';
import { AISuggestionStatusBadge } from './AISuggestionStatusBadge';
import { FieldRendererProps } from '../types';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui-kit/button';

/**
 * Date Field Renderer
 */
export function DateField({
  field,
  value,
  onChange,
  error,
  enrichmentContext,
  formData,
}: FieldRendererProps) {
  const showSuggestion = field.aiSuggestionEnabled && field.aiSuggestionKey;

  // Resolve template variables
  const resolvedLabel = resolveTemplateVariables(field.label, formData, enrichmentContext);
  const resolvedHelpText = resolveTemplateVariables(field.helpText, formData, enrichmentContext);

  // Check if suggestion is available and field is empty (for layout adjustments)
  const currentStr = value !== undefined && value !== null ? String(value) : '';

  // Check if value is "Upon Signature" (case-insensitive)
  const isUponSignature = currentStr.toLowerCase() === 'upon signature';
  const [showDateInput, setShowDateInput] = useState(!isUponSignature);

  // If value changes to a date, show date input; if it becomes "Upon Signature", hide date input
  useEffect(() => {
    if (currentStr && currentStr.toLowerCase() === 'upon signature') {
      setShowDateInput(false);
    } else if (currentStr && currentStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      setShowDateInput(true);
    }
  }, [currentStr]);

  // Handle switching from "Upon Signature" to date input
  const handleChangeToDate = () => {
    setShowDateInput(true);
    onChange(field.name, ''); // Clear the value so user can pick a date
  };

  return (
    <div className="space-y-2">
      <div className="flex  justify-between gap-4  items-start mb-2">
        <div>
          <div className="flex items-start justify-between gap-2">
            <Label
              htmlFor={field.name}
              className="text-base font-semibold font-nunito text-primary-light"
            >
              {resolvedLabel}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            {showSuggestion && (
              <AISuggestionStatusBadge
                suggestionKey={field.aiSuggestionKey as string}
                enrichmentContext={enrichmentContext}
                currentValue={value}
                className=" md:hidden mt-1"
              />
            )}
          </div>

          {resolvedHelpText && <p className="text-sm text-secondary-300">{resolvedHelpText}</p>}
        </div>
        {showSuggestion && (
          <AISuggestionStatusBadge
            suggestionKey={field.aiSuggestionKey as string}
            enrichmentContext={enrichmentContext}
            currentValue={value}
            className="hidden md:flex"
          />
        )}
      </div>
      <div className="relative">
        {isUponSignature && !showDateInput ? (
          // Display "Upon Signature" as a badge/display with option to change
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-lg border-2 border-selise-blue/30 bg-selise-blue/5 px-4 py-2.5 h-11 flex items-center">
              <span className="text-base font-medium text-[hsl(var(--fg))]">Upon Signature</span>
            </div>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleChangeToDate}
              className="h-11"
            >
              Change to Date
            </Button>
          </div>
        ) : (
          // Regular date input
          <>
            <Input
              id={field.name}
              type="date"
              value={(value as string) || ''}
              onChange={(e) => onChange(field.name, e.target.value)}
              className={cn(
                error ? 'border-destructive' : '',
                'shadow-none border border-secondary-100'
              )}
            />
            {showSuggestion && (
              <>
                {/* Desktop: Inside input */}
                <div className="absolute right-12 top-1/2 -translate-y-1/2 hidden sm:block">
                  <AISuggestionIndicator
                    suggestionKey={field.aiSuggestionKey as string}
                    enrichmentContext={enrichmentContext}
                    currentValue={value}
                    onApply={(suggestedValue) => onChange(field.name, suggestedValue)}
                    variant="inline"
                  />
                </div>
                {/* Mobile: Below input */}
                <div className="mt-2 sm:hidden">
                  <AISuggestionIndicator
                    suggestionKey={field.aiSuggestionKey as string}
                    enrichmentContext={enrichmentContext}
                    currentValue={value}
                    onApply={(suggestedValue) => onChange(field.name, suggestedValue)}
                    variant="inline"
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
